import Image from 'next/image';
import { OverlayProvider } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import { useRole } from '@/hooks/useRole';
import { Roles } from '@/constants/roles';
import { useIsKYBVerified } from '@/hooks/useIsKYBVerified';
import { Button, ButtonProps } from '../buttons';
import { Popup } from '../Popup';
import { InviteFriends, InvoiceToHostedPage } from '../popups';
import styles from './PageBanner.module.css';

export interface PageBannerProps {
  title?: string;
  description?: string;
  buttonLabel?: string;
  gradientSrc?: string;
  variants?: ButtonProps['variants'];
  href?: string;
  isInviteBanner?: boolean;
  isDisabled?: boolean;
  isFake?: boolean;
}

export const PageBanner = ({
  title,
  description,
  buttonLabel,
  gradientSrc,
  variants = 'whiteGreen',
  href,
  isInviteBanner,
  isDisabled = false,
  isFake = false,
}: PageBannerProps) => {
  const state = useOverlayTriggerState({});
  const role = useRole();
  const isKYBVerified = useIsKYBVerified({
    isEnabled: !isFake,
  });

  const renderPopupContent = () => {
    if (isInviteBanner || role === Roles.PERSONAL) {
      return <InviteFriends />;
    }

    if (role === Roles.BUSINESS && !isKYBVerified) {
      return <InviteFriends />;
    }

    if (role === Roles.BUSINESS && isKYBVerified) {
      return <InvoiceToHostedPage />;
    }

    return '';
  };

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          {description && <p className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />}
        </div>
        {href ? (
          <Button
            isDisabled={isDisabled}
            variants={variants}
            size="small"
            additionalBg="blue"
            className={styles.inviteButton}
            href={href}
            isExternal
          >
            {buttonLabel}
          </Button>
        ) : (
          <OverlayProvider>
            <Button
              isDisabled={isDisabled}
              variants={variants}
              size="small"
              additionalBg="blue"
              className={styles.inviteButton}
              onPress={state.open}
            >
              {buttonLabel}
            </Button>
            <Popup
              isOpen={state.isOpen}
              onClose={state.close}
              hasCloseIcon
              isDismissable
              dialogClassName={styles.popup}
            >
              {renderPopupContent()}
            </Popup>
          </OverlayProvider>
        )}
      </div>
      <Image src={gradientSrc || ''} alt="gradient" fill className={styles.image} priority loading="eager" />
    </div>
  );
};
